"use client";
import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Package, ShoppingCart } from "lucide-react";
import { useCart } from "../context/cartContext";
import { addToCartGA } from "../../../lib/analytics";
import {
  formatCategory,
  formatProductName,
  productPath,
} from "../../../lib/catalog";
import { WhatsAppLink } from "./StoreLinks";

export interface ShopProduct {
  id: string;
  name: string;
  shortDescription?: string;
  originalPrice: number;
  discountedPrice: number;
  images: string[];
  category: string;
  stock: number;
  createdAt: string;
  updatedAt: string;
}
export function ProductPhoto({
  src,
  alt,
  priority = false,
  sizes = "(min-width: 1024px) 25vw, 50vw",
}: {
  src?: string;
  alt: string;
  priority?: boolean;
  sizes?: string;
}) {
  const [failed, setFailed] = useState(false);
  return src && !failed ? (
    <Image
      src={src}
      alt={alt}
      fill
      sizes={sizes}
      priority={priority}
      fetchPriority={priority ? "high" : undefined}
      unoptimized={
        !src.startsWith("/") && !src.startsWith("https://res.cloudinary.com/")
      }
      className="product-photo-image"
      onError={() => setFailed(true)}
    />
  ) : (
    <div className="product-placeholder">
      <Package size={42} />
      <span>Image unavailable</span>
    </div>
  );
}
export default function ShopProductCard({ product }: { product: ShopProduct }) {
  const { addToCart } = useCart();
  const [pending, setPending] = useState(false);
  const [added, setAdded] = useState(false);
  const [error, setError] = useState("");
  const available = product.stock > 0;
  const productName = formatProductName(product.name);
  const href = productPath(product);
  const add = async () => {
    if (pending || !available) return;
    setPending(true);
    setError("");
    setAdded(false);
    try {
      const result = await addToCart(product.id, 1);
      if (result.success) {
        setAdded(true);
        addToCartGA(product.name, 1);
      } else setError(result.error || "Could not add this item. Try again.");
    } finally {
      setPending(false);
    }
  };
  return (
    <article className="shop-product-card">
      <Link
        href={href}
        className="product-image-link"
        aria-label={`View ${productName}`}
      >
        <ProductPhoto src={product.images[0]} alt={productName} />
      </Link>
      <div className="product-card-copy">
        <p className="product-category">{formatCategory(product.category)}</p>
        <h2>
          <Link href={href}>{productName}</Link>
        </h2>
        <div className="product-price">
          AED {product.discountedPrice.toFixed(2)}
          {product.originalPrice > product.discountedPrice && (
            <del>AED {product.originalPrice.toFixed(2)}</del>
          )}
        </div>
        <p
          className={`stock-status ${available ? "available" : "unavailable"}`}
        >
          {available
            ? product.stock < 5
              ? `Low stock (${product.stock} left)`
              : "In stock"
            : "Out of stock"}
        </p>
        {available ? (
          <button
            className="store-button primary"
            disabled={pending}
            onClick={add}
          >
            <ShoppingCart size={16} />
            {pending ? "Adding…" : added ? "Add another" : "Add to cart"}
          </button>
        ) : (
          <WhatsAppLink
            label="Check availability"
            source="out_of_stock_card"
            message={`Hello Shop House, ${productName} is shown as out of stock on your website. Is it available in store, or do you have a similar alternative?`}
          />
        )}
        <div className="product-feedback" aria-live="polite">
          {added && <Link href="/cart">Added to cart · View cart →</Link>}
          {error && <p role="alert">{error}</p>}
        </div>
      </div>
    </article>
  );
}
